/* Administration -> Webinar — GLOBAL webinar configuration only.
 *
 * Master switch, global defaults (nurture + per-reminder-kind switches),
 * test-mode safeguards, global WhatsApp template mapping/status, provider
 * configuration/status and integration diagnostics.
 *
 * Everything operational — campaign creation/list, funnel, registrations
 * and attendance, automation/messaging per campaign and the failure +
 * retry queue — lives on the top-level Webinars page (pages/WebinarsPage),
 * and is deliberately NOT duplicated here. Every write is gated
 * server-side on `webinar.config`; this component only hides what the
 * user cannot do.
 *
 * The master kill switch defaults OFF and is shown first, deliberately — a
 * campaign can be fully configured while the whole subsystem is still dark.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Video, ShieldCheck, MessageSquare, Stethoscope, ArrowRight } from "lucide-react";
import { ProviderCapabilityPanel, toneClass, useWebinarCaps } from "@/components/webinar/webinarShared";
import { WebinarPublicUrlCard } from "@/components/webinar/WebinarPublicUrlCard";
import { ErrorState, ListSkeleton } from "@/components/common";

const REMINDER_LABELS = {
  reminder_d1_2100: "D-1 21:00 IST",
  reminder_dayof_0900: "Event day 09:00 IST",
  reminder_t60: "60 minutes before",
  reminder_t15: "15 minutes before",
  reminder_ontime: "On time",
};

const OnOff = ({ on, onLabel = "on", offLabel = "off", danger = false, testId }) => (
  <Badge variant="outline" data-testid={testId}
         className={`text-[10px] ${toneClass(on ? (danger ? "danger" : "success") : "muted")}`}>
    {on ? onLabel : offLabel}
  </Badge>
);

/* ---------------------------------------------- master switch + defaults */
const SettingsCard = ({ settings, onSaved, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try {
      await api.put("/webinar/settings", patch);
      toast.success("Webinar settings saved");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="webinar-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Video className="h-4 w-4" /> Webinar automation — master switch &amp; global defaults
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Master switch</p>
            <p className="text-xs text-muted-foreground">
              Off by default. While this is off, no webinar enrollment, reminder, CTA or
              Anika follow-up happens at all — campaigns can still be configured.
            </p>
          </div>
          <Switch
            data-testid="webinar-master-switch"
            disabled={!canEdit || busy}
            checked={!!settings.enabled}
            onCheckedChange={(v) => save({ enabled: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Pre-webinar nurture (Live campaigns) — global default</p>
            <p className="text-xs text-muted-foreground">
              Up to 4 daily messages, all strictly before D-1. A campaign also needs its own nurture
              days (Webinars → Automation &amp; messaging).
            </p>
          </div>
          <Switch
            data-testid="webinar-nurture-switch"
            disabled={!canEdit || busy}
            checked={settings.nurture_enabled !== false}
            onCheckedChange={(v) => save({ nurture_enabled: v })}
          />
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Reminder kinds — global switches</p>
          <p className="text-[11px] text-muted-foreground">
            Turning a kind off here stops it for every campaign; each campaign can additionally
            turn it off for itself.
          </p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(settings.reminder_switches || {}).map(([kind, on]) => (
              <label key={kind} className="flex items-center gap-1.5 text-xs">
                <Switch
                  data-testid={`webinar-reminder-switch-${kind}`}
                  disabled={!canEdit || busy}
                  checked={on !== false}
                  onCheckedChange={(v) => save({ reminder_switches: { [kind]: v } })}
                />
                {REMINDER_LABELS[kind] || kind.replace(/_/g, " ")}
              </label>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* --------------------------------------------------- test-mode safeguards */
const SafeguardsCard = ({ settings }) => {
  const d = settings.diagnostics || {};
  const active = d.active_campaigns || {};
  return (
    <Card data-testid="webinar-safeguards-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Test-mode safeguards
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">WhatsApp (Interakt)</p>
            <OnOff on={settings.whatsapp_enabled} testId="webinar-safeguard-whatsapp" />
          </div>
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">Voice (ElevenLabs)</p>
            <OnOff on={settings.voice_enabled} testId="webinar-safeguard-voice" />
          </div>
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">TEST WhatsApp sends</p>
            <OnOff on={settings.allow_test_sends} onLabel="ALLOWED" offLabel="blocked" danger
                   testId="webinar-safeguard-test-sends" />
          </div>
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">TEST voice calls</p>
            <OnOff on={d.voice_allow_test_sends} onLabel="ALLOWED" offLabel="blocked" danger
                   testId="webinar-safeguard-test-calls" />
          </div>
        </div>
        <p data-testid="webinar-safeguard-active">
          Active campaigns: {active.total ?? 0} — {active.test_mode ?? 0} in TEST MODE,{" "}
          <span className={active.live_mode ? "text-[hsl(var(--danger))] font-medium" : ""}>
            {active.live_mode ?? 0} LIVE
          </span>.
        </p>
        <p className="text-[11px] text-muted-foreground">
          A campaign in TEST MODE always uses the simulated webinar provider and every record it
          creates is marked TEST; the delivery outbox independently refuses to dispatch TEST
          records while TEST sends are blocked. A LIVE campaign with an unusable provider cannot be
          activated — it is never silently replaced by the simulated one. Delivery providers and
          the TEST-send flags are managed in Integrations.
        </p>
      </CardContent>
    </Card>
  );
};

/* --------------------------------------------- global template mapping */
const TemplateStatusCard = ({ settings }) => {
  const rows = settings.template_status || [];
  const missing = rows.filter((r) => r.used_by_current_code && !r.configured).length;
  return (
    <Card data-testid="webinar-template-status-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> WhatsApp template mapping
          {missing > 0 && (
            <Badge variant="outline" className={`text-[10px] ${toneClass("warning")}`}>{missing} not mapped</Badge>
          )}
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          The default template kind for each webinar message and whether it is mapped to an approved
          Interakt template. This is a read-out of the ONE global registry — map (or change) a
          template only in Integrations → WhatsApp templates, and this page, the campaign readiness
          panel and the Integrations page all change together. An unmapped kind fails at send time
          and nothing is sent. Per-campaign template overrides are chosen under Webinars →
          Automation &amp; messaging.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Message</TableHead><TableHead>Template kind</TableHead>
                <TableHead>Interakt template</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.message_kind} data-testid={`webinar-template-row-${r.message_kind}`}>
                  <TableCell className="text-xs">{r.label}</TableCell>
                  <TableCell className="text-[11px] font-mono">{r.template_kind}</TableCell>
                  <TableCell className="text-xs">
                    {!r.used_by_current_code ? (
                      <span className="text-muted-foreground">Not sent — a no-show re-invite uses the target campaign&apos;s own messages</span>
                    ) : r.configured ? (
                      <span>{r.template_name} <span className="text-muted-foreground">({r.language || "en"})</span></span>
                    ) : (
                      <span className="text-[hsl(var(--danger))]">Not mapped</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {(settings.template_registry?.general_placeholders || []).length > 0 && (
          <div className="rounded-md border p-2 space-y-1" data-testid="webinar-template-placeholders">
            <p className="text-xs font-medium">General placeholders (not webinar messages)</p>
            <p className="text-[11px] text-muted-foreground">
              Same global registry — shown here so an unmapped placeholder is never mistaken for a mapped one.
            </p>
            {settings.template_registry.general_placeholders.map((p) => (
              <div key={p.template_kind} className="flex items-start gap-2 text-xs"
                   data-testid={`webinar-placeholder-${p.template_kind}`}>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${toneClass(p.mapped ? "success" : "danger")}`}>
                  {p.mapped ? "mapped" : "unmapped"}
                </Badge>
                <span>
                  <span className="font-medium">{p.label}</span>{" "}
                  <span className="text-muted-foreground">
                    ({p.template_kind}) — {p.used_by}
                    {p.mapped ? ` — ${p.template_name} (${p.language || "en"})` : ""}
                    {p.send_switch ? ` — ${p.send_switch}: ${p.send_enabled ? "on" : "off"}` : ""}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
        {!settings.webinar_link_base_url && (
          <p className="text-[11px] text-[hsl(var(--warning))]" data-testid="webinar-template-setup-missing">
            The dynamic URL-button base is not available until a valid public base URL is set (above).
          </p>
        )}
        {settings.webinar_link_base_url && (
          <div className="rounded-md border p-2 space-y-1" data-testid="webinar-template-setup">
            <p className="text-xs font-medium">Dynamic URL-button base</p>
            <p className="text-[11px] text-muted-foreground">
              Paste this as the dynamic URL-button base for every webinar template in
              Interakt&apos;s Template Manager — SalesOS appends each attendee&apos;s own
              token automatically:
            </p>
            <code className="block text-[11px] break-all bg-muted rounded px-1.5 py-1">
              {settings.webinar_link_base_url}
            </code>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------ integration diagnostics */
const DiagnosticsCard = ({ settings }) => {
  const d = settings.diagnostics || {};
  const secrets = d.webhook_secret_configured || {};
  const urls = d.attendance_webhook_urls || {};
  const providerLabel = Object.fromEntries((settings.provider_capabilities || []).map((r) => [r.provider, r.label]));
  return (
    <Card data-testid="webinar-diagnostics-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Stethoscope className="h-4 w-4" /> Integration diagnostics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">Interakt API key</p>
            <OnOff on={d.interakt_api_key_configured} onLabel="configured" offLabel="missing" testId="webinar-diag-interakt" />
          </div>
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">ElevenLabs agent (voice follow-up)</p>
            <OnOff on={d.elevenlabs_agent_configured} onLabel="configured" offLabel="missing" testId="webinar-diag-agent" />
          </div>
          <div className="rounded-md border p-2">
            <p className="text-[11px] text-muted-foreground">Call window (Ops policies)</p>
            <span data-testid="webinar-diag-call-window">
              {d.call_window?.start || "10:00"}–{d.call_window?.end || "21:30"} IST
            </span>
          </div>
        </div>
        <div>
          <p className="font-medium mb-1">Signed attendance webhooks</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Provider</TableHead><TableHead>Signing secret</TableHead><TableHead>Endpoint</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {Object.keys(secrets).map((p) => (
                  <TableRow key={p} data-testid={`webinar-diag-webhook-${p}`}>
                    <TableCell className="text-xs">{providerLabel[p] || p}</TableCell>
                    <TableCell>
                      <OnOff on={secrets[p]} onLabel="configured" offLabel="missing — deliveries rejected (401)" />
                    </TableCell>
                    <TableCell className="text-[11px] font-mono break-all">{urls[p] || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Only presence is shown — secret values are never returned to the browser.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const ProvidersCard = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <Card data-testid="webinar-providers-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading">Webinar providers — what SalesOS can actually do</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {rows.map((r) => (
          <ProviderCapabilityPanel key={r.provider} row={r} method={r.default_registration_method}
                                   testId={`webinar-provider-${r.provider}`} />
        ))}
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------ the tab */
// 2026-09 round 9 correction — the block above (explanatory text + "Open
// Webinars" link) used to be the ONLY thing that always rendered; every
// actual control was gated behind `{settings && (...)}`, so ANY failure of
// GET /webinar/settings (a 403 for a genuinely under-provisioned account, a
// transient 500, a network error) silently left the page looking like this
// stub is the whole page — with only an easy-to-miss toast as the only
// signal something went wrong. That is indistinguishable from "there is no
// config UI here", which is exactly the production symptom reported. Now
// tracked explicitly: `error` distinguishes "you don't have permission"
// (403 — never retried, since retrying won't help) from any other failure
// (loud ErrorState + Retry), and `loading` shows a real skeleton instead of
// a silent blank gap while the first request is in flight.
export const WebinarTab = () => {
  const { canConfig } = useWebinarCaps();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // { status, message } | null

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSettings((await api.get("/webinar/settings")).data);
    } catch (e) {
      const status = e?.response?.status;
      setError({ status, message: errDetail(e) });
      if (status !== 403) toast.error(errDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="webinar-admin">
      <div className="rounded-md border p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="webinar-admin-moved">
        <p className="text-xs text-muted-foreground">
          Campaigns, registrations &amp; attendance, per-campaign automation and the failure/retry
          queue are on the <span className="font-medium text-foreground">Webinars</span> page. This tab
          holds global webinar configuration only.
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to="/webinars" data-testid="webinar-admin-open-page">Open Webinars <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
        </Button>
      </div>
      {loading && <ListSkeleton rows={4} testId="webinar-admin-loading" />}
      {!loading && error?.status === 403 && (
        <ErrorState testId="webinar-admin-forbidden"
          title="You do not have permission to configure Webinar"
          message="Ask an administrator to grant webinar.config (or webinar.view for read-only access)." />
      )}
      {!loading && error && error.status !== 403 && (
        <ErrorState testId="webinar-admin-error"
          title="Couldn't load Webinar settings"
          message={error.message} onRetry={load} />
      )}
      {!loading && !error && settings && (
        <>
          <SettingsCard settings={settings} onSaved={load} canEdit={canConfig} />
          <WebinarPublicUrlCard publicUrl={settings.public_url} canEdit={canConfig} onSaved={load} />
          <SafeguardsCard settings={settings} />
          <TemplateStatusCard settings={settings} />
          <ProvidersCard rows={settings.provider_capabilities} />
          <DiagnosticsCard settings={settings} />
        </>
      )}
    </div>
  );
};

export default WebinarTab;
